import React, { useState, useCallback } from 'react';
import {
  Grid,
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Skeleton
} from '@esgian/esgianui';
import PropTypes from 'prop-types';
import VesselsMap from '../../../Maps/VesselsMap/VesselsMap';
import { useLookupCanalsQuery, useSegment } from '@hooks';
import { useTheme } from '@hooks/useTheme';
import { ProfileLink } from '@components/Links';
import { useNavigate } from 'react-router-dom';

function CanalsOverviewSection({
  canalArrivalVesselsData = null,
  canalInTransitVesselsData = null
}) {
  const vesselsData = [
    ...(canalArrivalVesselsData ? canalArrivalVesselsData : []),
    ...(canalInTransitVesselsData ? canalInTransitVesselsData : [])
  ];

  const { data: canals, isLoading: isLoadingCanals } = useLookupCanalsQuery();

  const { customScrollbar } = useTheme();
  const { uriExt } = useSegment();
  const [selectedCanal, setSelectedCanal] = useState(null);
  const [hoverCanal, setHoverCanal] = useState(null);
  const navigate = useNavigate();

  const handleCanalHover = (canal) => {
    if (canal) {
      setHoverCanal(canal);
    } else {
      setHoverCanal(null);
    }
  };

  const handleCanalClick = useCallback(
    (canal) => {
      setSelectedCanal(canal);

      if (canal && canal.id) {
        navigate(`${uriExt}/commercial-analytics/canal/${canal.regionId}`);
      }
    },
    [navigate]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper sx={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Canals</Typography>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto', ...customScrollbar }}>
            <List sx={{ p: 0, width: '100%' }}>
              {isLoadingCanals &&
                Array.apply(null, { length: 12 }).map((e, i) => (
                  <React.Fragment key={`skeleton-${i}`}>
                    <ListItem
                      sx={{
                        backgroundColor: ({ palette }) =>
                          i % 2 === 0 ? palette.background.paper : palette.background.default
                      }}>
                      <ListItemText>
                        <Skeleton width={'100%'} />
                      </ListItemText>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              {!isLoadingCanals &&
                canals?.map((canal, i) => (
                  <React.Fragment key={`${canal.id}-${i}`}>
                    <ListItem
                      onMouseEnter={() => handleCanalHover(canal)}
                      onMouseLeave={() => handleCanalHover(null)}
                      onClick={() => handleCanalClick(canal)}
                      sx={{
                        backgroundColor: ({ palette }) => {
                          if (selectedCanal?.id === canal.id) {
                            return palette.primary.light;
                          }
                          return i % 2 === 0
                            ? palette.background.paper
                            : palette.background.default;
                        },
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: ({ palette }) =>
                            selectedCanal?.id === canal.id
                              ? palette.primary.light
                              : palette.action.hover
                        }
                      }}>
                      <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                        <ProfileLink profile={'canal'} name={canal.name} id={canal.regionId} />
                      </ListItemText>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              {!canals?.length && !isLoadingCanals && (
                <ListItem>
                  <ListItemText>
                    <Typography>No matching results</Typography>
                  </ListItemText>
                </ListItem>
              )}
            </List>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={9}>
        <Paper sx={{ height: '90vh' }}>
          <VesselsMap
            loading={isLoadingCanals}
            showSpeedLegend
            height={'90vh'}
            zoom={selectedCanal ? 7 : 1}
            projection={'mercator'}
            vessels={vesselsData}
            center={selectedCanal ? [selectedCanal.long, selectedCanal.lat] : [0, 0]}
            regionPolygon={null}
            isCanal={true}
            canals={canals}
            selectedCanal={selectedCanal || hoverCanal}
            onCanalClick={handleCanalClick}
          />
        </Paper>
      </Grid>
    </Grid>
  );
}

CanalsOverviewSection.propTypes = {
  canalArrivalVesselsData: PropTypes.arrayOf(PropTypes.object),
  canalInTransitVesselsData: PropTypes.arrayOf(PropTypes.object)
};

export default CanalsOverviewSection;
